var _excluded = ["button", "disabled", "icon", "image", "label", "link", "onClick", "size"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React from 'react';
import { isIOS, isSafari } from 'react-device-detect';
import { useTheme } from '@veneer/theme';
import AVATAR_SIZES from './utils';
import cloneElementEmotion from '../utils/clone_element_emotion';
import highContrastMediaQuery from '../utils/high_contrast_media_query';
import propTypes from './prop_types';
import useStyles from './use_styles';
import { jsx as ___EmotionJSX } from "@emotion/react";
var Avatar = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var {
      button = false,
      disabled = false,
      icon,
      image,
      label,
      link,
      onClick,
      size = 36
    } = _ref,
    avatarProps = _objectWithoutProperties(_ref, _excluded);
  var iconSize = AVATAR_SIZES[size].icon;
  var labelSize = AVATAR_SIZES[size].label;
  var {
    lineHeight
  } = AVATAR_SIZES[size];
  var theme = useTheme();
  var isHc = theme.mode === 'contrast';
  var handleClick = React.useCallback(event => {
    if (disabled) return;
    onClick === null || onClick === void 0 || onClick(event);
  }, [disabled, onClick]);
  var handleOnKeyDown = React.useCallback(event => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      onClick === null || onClick === void 0 || onClick(event);
    }
  }, [disabled, onClick]);
  var isClickable = button || link;
  var elementTabIndex = isClickable && !disabled ? 0 : -1;
  var elementRole = button ? 'button' : 'img';
  var hasImage = !!image;
  var sizes = {
    default: size,
    label: labelSize,
    lineHeight: lineHeight
  };
  var styles = useStyles({
    sizes
  });
  var avatarContent = ___EmotionJSX(React.Fragment, null, image && ___EmotionJSX("img", {
    "aria-hidden": true,
    src: image
  }), !image && icon && /*#__PURE__*/React.cloneElement(icon, {
    customStyle: {
      height: iconSize,
      width: iconSize
    }
  }), !image && !icon && label && ___EmotionJSX("span", null, label.replace(/\s/g, '').slice(0, 2)));

  /**
   * To solve a Table issue  using Avatar (with css property) on React.cloneElement.
   * For reference: https://github.com/emotion-js/emotion/issues/1713
   */
  var avatarStyles = [styles.content.base, isClickable && styles.content.clickable, hasImage && styles.image.base, isHc && !disabled && isClickable && styles.content.contrastClickable, isHc && hasImage && styles.image.contrast, isHc && !isClickable && styles.content.contrastNotClickable, disabled && styles.content.disabled, isSafari && isIOS && isClickable && styles.root.appleInteractions, highContrastMediaQuery(theme)];
  var componentProps = {
    'aria-disabled': disabled,
    css: avatarStyles,
    disabled,
    onClick: handleClick,
    onKeyDown: handleOnKeyDown,
    ref
  };
  var interactiveContent = ___EmotionJSX("div", _extends({
    role: elementRole,
    tabIndex: elementTabIndex
  }, componentProps, avatarProps), avatarContent);
  var rootStyles = [styles.root.base, disabled && styles.root.disabled];
  if (button) return ___EmotionJSX("div", {
    css: rootStyles
  }, interactiveContent);
  if (link) return ___EmotionJSX("div", {
    css: rootStyles
  }, cloneElementEmotion(link, componentProps, avatarContent));
  return interactiveContent;
});
Avatar.propTypes = propTypes;
Avatar.displayName = 'Avatar';
export default Avatar;